/**
 * Authentication Context
 * Manages user authentication state (email + password)
 */
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { intervalManager } from '../utils/intervalManager';

const AuthContext = createContext(null);

const NOTICE_KEY = 'auth_notice';

// The server's refusal as { message, code }: sign-in/sign-up send
// { code, message }; validation errors send a list.
export const authError = (data, fallback) => {
  const detail = data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    return { message: detail.message || fallback, code: detail.code };
  }
  if (Array.isArray(detail)) {
    return { message: detail.map((d) => String(d.msg).replace(/^Value error, /, '')).join(', ') };
  }
  return { message: detail || fallback };
};

// Why the app signed someone out (account disabled, still waiting for
// approval), shown once on the login page.
export const setAuthNotice = (data) => {
  const detail = data?.detail;
  if (typeof detail !== 'string' || !/disabled|approval/i.test(detail)) return;
  try {
    sessionStorage.setItem(NOTICE_KEY, JSON.stringify({ message: detail, code: /approval/i.test(detail) ? 'pending' : 'disabled' }));
  } catch (_) {}
};

export const takeAuthNotice = () => {
  try {
    const notice = JSON.parse(sessionStorage.getItem(NOTICE_KEY) || 'null');
    sessionStorage.removeItem(NOTICE_KEY);
    return notice;
  } catch (_) {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_URL || (
    window.location.hostname === 'localhost'
      ? 'http://localhost:8000/api'
      : '/api'
  );

  const fetchUserData = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser({ ...userData, authenticated: true });
        return true;
      } else {
        setAuthNotice(await response.json().catch(() => null));
        localStorage.removeItem('access_token');
        setUser(null);
        return false;
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      localStorage.removeItem('access_token');
      setUser(null);
      return false;
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchUserData(token).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchUserData]);

  // Tabs share one sign-in (localStorage). When another tab signs in as
  // someone else or signs out, follow it right away, so this tab never acts
  // as the previous account (e.g. still showing admin pages).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'access_token') return;
      if (e.newValue) fetchUserData(e.newValue);
      else setUser(null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchUserData]);

  const loginWithToken = useCallback((token) => {
    localStorage.setItem('access_token', token);
    fetchUserData(token);
    // Notify sidebar to show "Just now" — backend triggers ETL on every login
    window.dispatchEvent(new CustomEvent('etl:started'));
  }, [fetchUserData]);

  // Email + password. Errors carry the backend's message in err.message and
  // its code (no_account, no_password, pending, disabled, exists, ...) in err.code.
  const postJson = useCallback(async (path, body) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const { message, code } = authError(data, 'Something went wrong. Please try again.');
      const err = new Error(message);
      err.code = code;
      throw err;
    }
    return data;
  }, [API_BASE_URL]);

  const login = useCallback(async (email, password) => {
    const data = await postJson('/auth/login', { email, password });
    loginWithToken(data.access_token);
    return data;
  }, [postJson, loginWithToken]);

  // A new address waits for an admin to approve it, so there's no login to do
  // yet; an address an admin already added signs in straight away.
  const signup = useCallback(async (email, password, fullName) => {
    const created = await postJson('/auth/signup', { email, password, full_name: fullName });
    if (created.status !== 'active') return { pending: true };
    return login(email, password);
  }, [postJson, login]);

  const logout = useCallback(async () => {
    intervalManager.clearAll();
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('access_token');
    }
  }, [API_BASE_URL]);

  const refreshUser = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (token) return fetchUserData(token);
    return Promise.resolve(false);
  }, [fetchUserData]);

  const value = {
    user,
    loading,
    loginWithToken,
    login,
    signup,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    needsProfileCompletion: !!user && !user.full_name,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
